import { Search, SlidersHorizontal, X } from "lucide-react";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { brandOptions, priceCeilings, sortOptions } from "../data/vehicles";

export const FilterBar = ({
  search,
  setSearch,
  brand,
  setBrand,
  maxPrice,
  setMaxPrice,
  sort,
  setSort,
  resultCount,
  totalCount,
  onReset,
}) => {
  const hasActiveFilters =
    search !== "" || brand !== "Alle merken" || maxPrice !== "any";

  return (
    <section
      data-testid="filter-bar"
      className="sticky top-20 z-30 bg-ink-950/95 backdrop-blur-xl border-b border-white/[0.06]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 py-6 md:py-7">
        <div className="flex flex-col lg:flex-row lg:items-end gap-5 lg:gap-6">
          {/* Title */}
          <div className="flex items-center gap-3 lg:mr-4">
            <SlidersHorizontal size={14} className="text-champagne" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-zinc-400 font-body">
              Filter &amp; Sorteer
            </span>
          </div>

          {/* Search */}
          <div className="flex flex-col gap-2 flex-1 min-w-[200px]">
            <label
              htmlFor="brand-search"
              className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body"
            >
              Zoek op merk / model
            </label>
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
              />
              <Input
                id="brand-search"
                data-testid="filter-search-input"
                type="text"
                placeholder="bijv. Ferrari, Porsche..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-11 bg-ink-900 border-white/10 hover:border-white/25 focus:border-champagne focus-visible:ring-0 focus-visible:ring-offset-0 text-sm font-body text-white placeholder:text-zinc-600 rounded-none"
              />
            </div>
          </div>

          {/* Brand select */}
          <div className="flex flex-col gap-2 min-w-[180px]">
            <label className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
              Merk
            </label>
            <Select value={brand} onValueChange={setBrand}>
              <SelectTrigger
                data-testid="filter-brand-trigger"
                className="h-11 bg-ink-900 border-white/10 hover:border-white/25 focus:border-champagne focus:ring-0 focus:ring-offset-0 text-sm font-body text-white rounded-none data-[placeholder]:text-zinc-600"
              >
                <SelectValue placeholder="Alle merken" />
              </SelectTrigger>
              <SelectContent
                data-testid="filter-brand-content"
                className="bg-ink-900 border-white/10 rounded-none"
              >
                {brandOptions.map((b) => (
                  <SelectItem
                    key={b}
                    value={b}
                    data-testid={`filter-brand-option-${b.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm font-body text-zinc-200 focus:bg-champagne/10 focus:text-champagne rounded-none"
                  >
                    {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Price ceiling */}
          <div className="flex flex-col gap-2 min-w-[180px]">
            <label className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
              Prijs tot
            </label>
            <Select value={maxPrice} onValueChange={setMaxPrice}>
              <SelectTrigger
                data-testid="filter-price-trigger"
                className="h-11 bg-ink-900 border-white/10 hover:border-white/25 focus:border-champagne focus:ring-0 focus:ring-offset-0 text-sm font-body text-white rounded-none"
              >
                <SelectValue placeholder="Elke prijs" />
              </SelectTrigger>
              <SelectContent
                data-testid="filter-price-content"
                className="bg-ink-900 border-white/10 rounded-none"
              >
                {priceCeilings.map((p) => (
                  <SelectItem
                    key={p.value}
                    value={p.value}
                    data-testid={`filter-price-option-${p.value}`}
                    className="text-sm font-body text-zinc-200 focus:bg-champagne/10 focus:text-champagne rounded-none"
                  >
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sort */}
          <div className="flex flex-col gap-2 min-w-[200px]">
            <label className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
              Sortering
            </label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger
                data-testid="filter-sort-trigger"
                className="h-11 bg-ink-900 border-white/10 hover:border-white/25 focus:border-champagne focus:ring-0 focus:ring-offset-0 text-sm font-body text-white rounded-none"
              >
                <SelectValue placeholder="Nieuwste eerst" />
              </SelectTrigger>
              <SelectContent
                data-testid="filter-sort-content"
                className="bg-ink-900 border-white/10 rounded-none"
              >
                {sortOptions.map((o) => (
                  <SelectItem
                    key={o.value}
                    value={o.value}
                    data-testid={`filter-sort-option-${o.value}`}
                    className="text-sm font-body text-zinc-200 focus:bg-champagne/10 focus:text-champagne rounded-none"
                  >
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Result count + reset */}
        <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
          <p
            data-testid="filter-result-count"
            className="text-[11px] tracking-[0.25em] uppercase text-zinc-500 font-body"
          >
            <span className="text-champagne">
              {String(resultCount).padStart(2, "0")}
            </span>
            <span className="mx-2 text-zinc-700">/</span>
            <span>{String(totalCount).padStart(2, "0")} voertuigen</span>
          </p>
          {hasActiveFilters && (
            <button
              type="button"
              data-testid="filter-reset-button"
              onClick={onReset}
              className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-zinc-400 hover:text-champagne transition-colors font-body"
            >
              <X size={12} />
              Filters wissen
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default FilterBar;
